import api from './client';
import { notifyItemSeen } from './newCounts';
import { uploadSrc } from './uploadUrl';

export type ApplicationStatus =
  | 'submitted'
  | 'under_review'
  | 'approved'
  | 'rejected'
  | 'withdrawn';

export const APPLICATION_STATUSES: ApplicationStatus[] = [
  'submitted',
  'under_review',
  'approved',
  'rejected',
  'withdrawn',
];

export interface ApplicationListing {
  id: number;
  title: string | null;
  address: string | null;
  type: string | null;
  price: string | number | null;
  beds: number | null;
  baths: number | null;
  coverPhoto: string | null;
  userId: number;
}

export interface ApplicationUser {
  id: number;
  name: string | null;
  email: string;
}

/** One entry in a rental application's reference list. */
export interface ApplicationReference {
  name?: string | null;
  relationship?: string | null;
  phone?: string | null;
  email?: string | null;
}

export interface RentalApplication {
  id: number;
  referenceNo: string | null;
  /** Admin list only: no admin has opened it yet. */
  isNew?: boolean;
  listingId: number;
  userId: number;
  agentId: number | null;
  fullName: string;
  phone: string | null;
  currentAddress: string | null;
  employmentStatus: string | null;
  employerName: string | null;
  jobTitle: string | null;
  monthlyIncome: string | number | null;
  previousLandlordName: string | null;
  tenancyDates: string | null;
  applicationFee: string | number | null;
  currency: string;
  paidAt: string | null;
  status: ApplicationStatus;
  createdAt?: string;
  updatedAt?: string;
  listing?: ApplicationListing;
  applicant?: ApplicationUser;
  agent?: ApplicationUser;

  /* The rest of what the applicant submitted. The list endpoint serialises the
     whole row (rentalApplication.controller.js serializeApplication), so these
     already arrive; they simply were not typed before. The *_ arrays hold
     absolute upload URLs — pass each through fileSrc() before rendering. */
  dateOfBirth?: string | null;
  durationAtAddress?: string | null;
  proofOfIncome?: string[];
  references?: ApplicationReference[];
  governmentId?: string[];
  socialSecurityCard?: string[];
  documents?: string[];
  paymentMethod?: string | null;
  certified?: boolean;
  feePaid?: boolean;
}

/** Resolve a stored document/photo URL for <img src> or a link (see uploadSrc). */
export const fileSrc = uploadSrc;

export interface ApplicationListParams {
  status?: ApplicationStatus;
  listingId?: number;
  search?: string;
  page?: number;
  limit?: number;
}

export interface ApplicationListResponse {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  applications: RentalApplication[];
}

interface RawListResponse extends ApplicationListResponse {
  status: number;
}

interface MutationResponse {
  status: number;
  message?: string;
  error?: string | string[];
  application?: RentalApplication;
}

/** GET /api/applications — admin sees all; agent sees their listings' apps. */
export const listApplications = async (
  params: ApplicationListParams = {}
): Promise<ApplicationListResponse> => {
  const query: Record<string, string> = {};
  if (params.status) query.status = params.status;
  if (params.listingId !== undefined) query.listingId = String(params.listingId);
  if (params.search) query.search = params.search;
  if (params.page) query.page = String(params.page);
  if (params.limit) query.limit = String(params.limit);

  const res = await api.get<RawListResponse>('/applications', { params: query });
  return {
    total: res.data.total,
    page: res.data.page,
    limit: res.data.limit,
    totalPages: res.data.totalPages,
    applications: res.data.applications,
  };
};

/** GET /api/applications/:id — admin, owning agent or the applicant. */
export const getApplication = async (id: number): Promise<RentalApplication> => {
  const res = await api.get<{ status: number; application: RentalApplication }>(`/applications/${id}`);
  notifyItemSeen();
  return res.data.application;
};

/** PATCH /api/applications/:id/status — admin or owning agent. */
export const updateApplicationStatus = async (
  id: number,
  status: ApplicationStatus
): Promise<RentalApplication> => {
  const res = await api.patch<MutationResponse>(`/applications/${id}/status`, {
    status,
  });
  if (!res.data.application) throw new Error('No application returned');
  notifyItemSeen();
  return res.data.application;
};

/** Human-readable label, e.g. 'under_review' -> 'Under Review'. */
export const statusLabel = (s: ApplicationStatus): string =>
  s
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

/** Maps a status to one of the app's badge-* classes. */
export const statusBadgeClass = (s: ApplicationStatus): string => {
  const map: Record<ApplicationStatus, string> = {
    submitted: 'badge-new',
    under_review: 'badge-pending',
    approved: 'badge-active',
    rejected: 'badge-rejected',
    withdrawn: 'badge-rejected',
  };
  return `badge ${map[s]}`;
};

/** Formats the backend's stringified DECIMAL into "$4,800" / "—". */
export const formatMoney = (value: string | number | null): string => {
  if (value === null || value === undefined || value === '') return '—';
  const num = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(num)) return String(value);
  return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 2 });
};

/** Pulls a human-readable error message out of an axios error. */
export const extractApiError = (err: unknown, fallback = 'Request failed'): string => {
  const e = err as {
    response?: { data?: { error?: string | string[]; message?: string } };
    message?: string;
  };
  const data = e?.response?.data;
  if (data?.error) {
    return Array.isArray(data.error) ? data.error.join(', ') : data.error;
  }
  if (data?.message) return data.message;
  if (e?.message) return e.message;
  return fallback;
};
